import type { GetContentResponse } from '@/services/api/api.types'
import { AbortedError, sleep } from '@/utils/bulkUploadTask'

export type ScrapeVerdict =
  | { kind: 'failed'; progress: NonNullable<GetContentResponse['processProgress']> }
  | { kind: 'finished' }
  | { kind: 'gone' }
  | { kind: 'undecided' }

// The backend writes a verdict 3-15s after the URL is added (C2-2644). Past the limit the
// expert is let go, and the file list reports the outcome as it always has.
export const VERDICT_LIMIT_MS = 15_000
const POLL_INTERVAL_MS = 1_000

export async function awaitScrapeVerdict({
  getStatus,
  signal,
  limitMs = VERDICT_LIMIT_MS,
  intervalMs = POLL_INTERVAL_MS,
}: {
  // `/content/get` answers `null` once the step is gone.
  getStatus: () => Promise<Pick<GetContentResponse, 'inProcess' | 'processProgress'> | null>
  signal?: AbortSignal
  limitMs?: number
  intervalMs?: number
}): Promise<ScrapeVerdict> {
  const deadline = Date.now() + limitMs

  while (Date.now() < deadline) {
    try {
      await sleep(intervalMs, signal)
    } catch (error) {
      if (error instanceof AbortedError) break
      throw error
    }

    let status
    try {
      status = await getStatus()
    } catch {
      // A failed poll says nothing about the scrape, so keep waiting until the limit.
      continue
    }
    if (!status) return { kind: 'gone' }
    if (status.processProgress?.error) return { kind: 'failed', progress: status.processProgress }
    if (!status.inProcess) return { kind: 'finished' }
  }

  return { kind: 'undecided' }
}
